// src/components/Admin/shared/FileUploader.jsx
import React, { useState, useRef, useCallback } from "react";
import { Upload, X, File, Check, AlertCircle, Loader2 } from "lucide-react";

/**
 * Reusable drag-and-drop file uploader.
 *
 * Props:
 * - onUpload: async (file) => void — called when file is selected/dropped
 * - accept: string — file input accept attribute (e.g., ".json,.pdf")
 * - acceptLabel: string — human-readable label (e.g., "JSON files")
 * - maxSizeMB: number — max file size in MB (default 50)
 * - currentFile: string | null — name of currently uploaded file (shows replace state)
 * - compact: boolean — smaller inline variant (default false)
 * - disabled: boolean
 */
const FileUploader = ({
  onUpload,
  accept = "*",
  acceptLabel = "Any file",
  maxSizeMB = 50,
  currentFile = null,
  compact = false,
  disabled = false,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const inputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) return "No file selected";

    // Size check
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max: ${maxSizeMB}MB`;
    }

    // Extension check
    if (accept && accept !== "*") {
      const allowedExts = accept.split(",").map((e) => e.trim().toLowerCase());
      const fileExt = `.${file.name.split(".").pop().toLowerCase()}`;
      if (!allowedExts.some((ext) => ext === fileExt || ext === file.type)) {
        return `Invalid file type. Expected: ${acceptLabel}`;
      }
    }

    return null;
  };

  const handleFile = useCallback(
    async (file) => {
      setError(null);
      setSuccess(null);

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setUploading(true);
      try {
        await onUpload(file);
        setSuccess(file.name);
        // Clear success after 3s
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [onUpload, accept, maxSizeMB],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled || uploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile, disabled, uploading],
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !uploading) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  // ---- Compact variant ----
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || uploading}
        />

        {uploading ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <Loader2 size={14} className="animate-spin" />
            Uploading...
          </span>
        ) : success ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
            <Check size={14} />
            Uploaded
          </span>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-300 hover:border-gray-400 rounded-lg transition disabled:opacity-50"
          >
            <Upload size={12} />
            {currentFile ? "Replace" : "Upload"}
          </button>
        )}

        {error && (
          <span className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle size={12} />
            {error}
          </span>
        )}
      </div>
    );
  }

  // ---- Full drop zone variant ----
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
          disabled
            ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
            : dragOver
              ? "border-secondary bg-secondary/5"
              : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={24} className="animate-spin text-secondary" />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload
              size={24}
              className={dragOver ? "text-secondary" : "text-gray-400"}
            />
            <div>
              <p className="text-sm text-gray-600">
                <span className="font-medium text-secondary">
                  Click to upload
                </span>{" "}
                or drag and drop
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {acceptLabel} — max {maxSizeMB}MB
              </p>
            </div>
          </div>
        )}

        {/* Current file indicator */}
        {currentFile && !uploading && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded text-xs text-gray-600">
              <File size={12} />
              {currentFile}
              <span className="text-emerald-500">✓</span>
            </div>
          </div>
        )}
      </div>

      {/* Success */}
      {success && (
        <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
          <Check size={14} />
          Uploaded: {success}
        </p>
      )}

      {/* Error */}
      {error && (
        <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={14} />
          {error}
        </p>
      )}
    </div>
  );
};

export default FileUploader;
